import { Card } from "@/components/ui/card";
import { Quote, Sparkles } from "lucide-react";
import Fuse from "fuse.js";

interface SpotlightContextCardProps {
    opportunity: {
        id?: string;
        type?: string;
        context_score?: number;
        script_location?: string;
        description?: string;
        // Legacy support for old format
        scene?: string;
        opportunity?: string;
        quote?: string;
    };
    scriptText: string | null;
    isScriptLoading: boolean;
}

const SpotlightContextCard = ({ opportunity, scriptText, isScriptLoading }: SpotlightContextCardProps) => {

    if (!opportunity) {
        return null; // Don't render anything if there's no opportunity
    }

    const getSpotlightContext = () => {
        if (!scriptText) {
            return {
                context: "Script context is not available.",
            };
        }

        const searchText = opportunity.quote || opportunity.script_location || "";
        if (!searchText) {
            return {
                context: "Could not find the spotlight in the script.",
            };
        }

        const matchIndex = scriptText.toLowerCase().indexOf(searchText ? searchText.toLowerCase() : "");
        const matchLength = searchText.length;

        if (matchIndex === -1) {
            const fallbackSearchText = opportunity.quote || opportunity.script_location || opportunity.description || "";
            const scriptLines = scriptText.split('\n').filter(line => line.trim().length > 0);
            const searchWords = fallbackSearchText ? fallbackSearchText.toLowerCase().split(/\s+/) : [];

            const highlightBestSentence = (line, words) => {
                // Split by sentences, keeping delimiters.
                const sentences = line.match(/[^.!?]+[.!?]*/g) || [line];

                if (sentences.length <= 1) {
                    return <span className="bg-yellow-100 font-bold px-1 rounded mx-0.5 text-black border border-yellow-200 shadow-sm">{line}</span>;
                }

                let bestSentenceIndex = -1;
                let maxScore = 0;

                sentences.forEach((sentence, index) => {
                    const sentenceWords = sentence.toLowerCase().split(/\s+/);
                    const score = words.filter(word => sentenceWords.includes(word)).length;
                    if (score > maxScore) {
                        maxScore = score;
                        bestSentenceIndex = index;
                    }
                });

                return sentences.map((sentence, index) => {
                    if (index === bestSentenceIndex) {
                        return <span key={index} className="bg-yellow-100 font-bold px-1 rounded mx-0.5 text-black border border-yellow-200 shadow-sm">{sentence}</span>;
                    }
                    return <span key={index}>{sentence}</span>;
                });
            };

            // 1. Try fuzzy search with fuse.js first
            const fuseOptions = { includeScore: true, threshold: 0.6 };
            const fuse = new Fuse(scriptLines, fuseOptions);
            const fuseResults = fuse.search(fallbackSearchText);

            if (fuseResults.length > 0) {
                const bestMatch = fuseResults[0];
                const bestMatchLine = bestMatch.item;
                const bestMatchIndex = bestMatch.refIndex;
                const linesBefore = scriptLines.slice(Math.max(0, bestMatchIndex - 2), bestMatchIndex);
                const linesAfter = scriptLines.slice(bestMatchIndex + 1, bestMatchIndex + 3);

                return {
                    context: (
                        <>
                            {linesBefore.length > 0 && <span className="opacity-70">{linesBefore.join("\n") + "\n"}</span>}
                            {highlightBestSentence(bestMatchLine, searchWords)}
                            {linesAfter.length > 0 && <span className="opacity-70">{"\n" + linesAfter.join("\n")}</span>}
                        </>
                    )
                };
            }

            // 2. If fuse.js fails, fall back to simple word counting
            let bestMatchByWordCount = { lineIndex: -1, score: 0, line: "" };
            scriptLines.forEach((line, index) => {
                const lineWords = line.toLowerCase().split(/\s+/);
                const score = searchWords.filter(word => lineWords.includes(word)).length;
                if (score > bestMatchByWordCount.score) {
                    bestMatchByWordCount = { lineIndex: index, score, line };
                }
            });

            if (bestMatchByWordCount.score >= 2) {
                const { line: bestLine, lineIndex: bestIndex } = bestMatchByWordCount;
                const linesBefore = scriptLines.slice(Math.max(0, bestIndex - 2), bestIndex);
                const linesAfter = scriptLines.slice(bestIndex + 1, bestIndex + 3);
                return {
                    context: (
                        <>
                            {linesBefore.length > 0 && <span className="opacity-70">{linesBefore.join("\n") + "\n"}</span>}
                            {highlightBestSentence(bestLine, searchWords)}
                            {linesAfter.length > 0 && <span className="opacity-70">{"\n" + linesAfter.join("\n")}</span>}
                        </>
                    )
                };
            }

            // 3. If both fail, return not found
            return { context: "Could not find the spotlight in the script." };
        }

        // Exact match found, proceed with original logic
        const lastNewlineBeforeMatch = scriptText.lastIndexOf('\n', matchIndex);
        const startOfMatchLine = lastNewlineBeforeMatch === -1 ? 0 : lastNewlineBeforeMatch + 1;

        const firstNewlineAfterMatch = scriptText.indexOf('\n', matchIndex + matchLength);
        const endOfMatchLine = firstNewlineAfterMatch === -1 ? scriptText.length : firstNewlineAfterMatch;

        const fullMatchLine = scriptText.substring(startOfMatchLine, endOfMatchLine);

        const textBeforeMatchLine = scriptText.substring(0, startOfMatchLine);
        const linesBefore = textBeforeMatchLine.split('\n').slice(-3, -1);
        const beforeText = linesBefore.join('\n') + (linesBefore.length > 0 ? '\n' : '');

        const textAfterMatchLine = scriptText.substring(endOfMatchLine);
        const linesAfter = textAfterMatchLine.split('\n').slice(1, 3);
        const afterText = (linesAfter.length > 0 ? '\n' : '') + linesAfter.join('\n');

        const relativeMatchStart = matchIndex - startOfMatchLine;
        const linePreMatch = fullMatchLine.substring(0, relativeMatchStart);
        const lineMatch = fullMatchLine.substring(relativeMatchStart, relativeMatchStart + matchLength);
        const linePostMatch = fullMatchLine.substring(relativeMatchStart + matchLength);

        return {
            context: (
                <>
                    <span className="opacity-70">{beforeText}</span>
                    <span className="opacity-70">{linePreMatch}</span>
                    <span className="bg-yellow-100 font-bold px-1 rounded mx-0.5 text-black border border-yellow-200 shadow-sm">{lineMatch}</span>
                    <span className="opacity-70">{linePostMatch}</span>
                    <span className="opacity-70">{afterText}</span>
                </>
            )
        };
    };

    const { context } = getSpotlightContext();

    return (
        <Card className="p-0 bg-white shadow-sm border border-[oklch(0.92_0_0)] rounded-[24px] overflow-hidden">
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-100 flex items-center gap-3 bg-white">
                <div className="bg-black p-2 rounded-lg">
                    <Quote className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[oklch(0.2686_0_0)]">What Artie Saw In Your Script</h3>
            </div>

            <div className="flex flex-col md:flex-row">
                {/* LEFT PANE: Script Context */}
                <div className="flex-1 p-8 bg-[#FAF9F6] border-r border-gray-100 min-h-[250px]">
                    <span className="text-xs font-bold text-[oklch(0.55_0_0)] uppercase tracking-wider mb-4 block">
                        Script Context
                    </span>

                    {isScriptLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="flex flex-col items-center gap-3">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                                <p className="text-xs text-gray-500">Extracting context...</p>
                            </div>
                        </div>
                    ) : (
                        <div className="font-mono text-sm leading-relaxed text-gray-800 whitespace-pre-wrap pl-4 border-l-2 border-gray-200">
                            {context}
                        </div>
                    )}
                </div>

                {/* RIGHT PANE: Analysis */}
                <div className="w-full md:w-[320px] p-8 bg-white flex flex-col justify-center">
                    <div className="space-y-6">
                        <div>
                            <span className="text-xs font-bold text-[oklch(0.55_0_0)] uppercase tracking-wider mb-2 block">
                                Identified Opportunity
                            </span>
                            <div className="text-xl font-bold text-[oklch(0.2686_0_0)] leading-tight">
                                {opportunity.opportunity ||
                                    (opportunity.description ?
                                        opportunity.description.replace(/^.*?'([^']+)'.*$/, '$1') ||
                                        opportunity.description.split(' ').slice(-2).join(' ')
                                        : 'Brand Integration Opportunity')}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-yellow-500" />
                                <span className="text-xs font-bold text-[oklch(0.55_0_0)] uppercase tracking-wider">
                                    Why it works
                                </span>
                            </div>
                            <p className="text-sm text-[oklch(0.45_0_0)] leading-relaxed">
                                This moment naturally fits the narrative, offering high visibility for aligned brands without disrupting the story flow.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default SpotlightContextCard;